import BusinessIcon from '@mui/icons-material/Business';
import ContactsIcon from '@mui/icons-material/Contacts';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import ForumIcon from '@mui/icons-material/Forum';
import GroupIcon from '@mui/icons-material/Group';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import TimelineIcon from '@mui/icons-material/Timeline';
import WorkIcon from '@mui/icons-material/Work';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { useState, type ReactElement } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { useSessie } from '../auth/SessieContext.tsx';
import { initialen } from '../helpers/formatteer.ts';

const breedte = 248;

interface Navigatie {
  pad: string;
  label: string;
  icoon: ReactElement;
  alleenBeheerder?: boolean;
}

const navigatie: Navigatie[] = [
  { pad: '/', label: 'Dashboard', icoon: <DashboardIcon /> },
  { pad: '/kandidaten', label: 'Kandidaten', icoon: <PeopleIcon /> },
  { pad: '/vacatures', label: 'Vacatures', icoon: <WorkIcon /> },
  { pad: '/voordrachten', label: 'Voordrachten', icoon: <TimelineIcon /> },
  { pad: '/bedrijven', label: 'Bedrijven', icoon: <BusinessIcon /> },
  { pad: '/contactpersonen', label: 'Contactpersonen', icoon: <ContactsIcon /> },
  { pad: '/gesprekken', label: 'Gesprekken', icoon: <ForumIcon /> },
  { pad: '/cvs', label: 'Cv-wachtrij', icoon: <DescriptionIcon /> },
  { pad: '/referentie', label: 'Referentielijsten', icoon: <ListAltIcon /> },
  { pad: '/gebruikers', label: 'Gebruikers', icoon: <GroupIcon />, alleenBeheerder: true },
];

/**
 * De omlijsting van de app: een balk bovenaan, navigatie links en de pagina in het midden.
 * @returns De schil met de actieve pagina.
 */
export function AppSchil() {
  const { ik, isBeheerder, meldAf } = useSessie();
  const locatie = useLocation();
  const navigeer = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [gebruikersmenu, setGebruikersmenu] = useState<HTMLElement | null>(null);

  const isActief = (pad: string) => (pad === '/' ? locatie.pathname === '/' : locatie.pathname.startsWith(pad));

  const inhoud = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ gap: 1.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
          }}
        >
          S
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Solvio Works
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ px: 1, flex: 1 }}>
        {navigatie
          .filter((item) => !item.alleenBeheerder || isBeheerder)
          .map((item) => (
            <ListItemButton
              key={item.pad}
              component={Link}
              to={item.pad}
              nativeButton={false}
              selected={isActief(item.pad)}
              onClick={() => setMenuOpen(false)}
              sx={{ borderRadius: 1.5, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icoon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
      </List>
    </Box>
  );

  const naam = ik?.gebruiker.naam ?? '';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          width: { md: `calc(100% - ${breedte}px)` },
          ml: { md: `${breedte}px` },
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => setMenuOpen(true)}
            sx={{ mr: 1, display: { md: 'none' } }}
            aria-label="Menu openen"
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flex: 1 }} />
          <IconButton
            onClick={(gebeurtenis) => setGebruikersmenu(gebeurtenis.currentTarget)}
            aria-label="Gebruikersmenu"
          >
            <Avatar
              sx={{ width: 34, height: 34, bgcolor: 'secondary.main', fontSize: 14 }}
              src={ik?.gebruiker.profielfotoUrl ?? undefined}
            >
              {initialen(naam)}
            </Avatar>
          </IconButton>
          <Menu anchorEl={gebruikersmenu} open={Boolean(gebruikersmenu)} onClose={() => setGebruikersmenu(null)}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2">{naam}</Typography>
              <Typography variant="caption" color="text.secondary">
                {ik?.gebruiker.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setGebruikersmenu(null);
                void navigeer('/profiel');
              }}
            >
              Mijn profiel
            </MenuItem>
            <MenuItem
              onClick={() => {
                setGebruikersmenu(null);
                void meldAf();
              }}
            >
              Afmelden
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { md: breedte }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          slotProps={{ root: { keepMounted: true } }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: breedte } }}
        >
          {inhoud}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: breedte, borderRight: 1, borderColor: 'divider' },
          }}
        >
          {inhoud}
        </Drawer>
      </Box>
      <Box component="main" sx={{ flex: 1, minWidth: 0, p: { xs: 2, md: 3 }, pt: { xs: 10, md: 11 } }}>
        <Outlet />
      </Box>
    </Box>
  );
}
